import './payment-method-update-sheet.scss';

import type { ReactElement } from 'react';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import {
  FormSubmit,
  type FormSubmitProps,
} from '@sola/ark-core/molecules/form/form.tsx';
import {
  Sheet,
  SheetActions,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTrigger,
} from '@sola/ark-core/molecules/sheet/sheet.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { usePaymentMethodUpdate } from '@sola/my-sdk/payment-methods/hooks.ts';
import { BillingFieldsEnum } from '@sola/schema/dotcom/checkout/billingInfo.ts';
import {
  AddressSelectOptions,
  BillingAddressEnum,
} from '@sola/schema/dotcom/shared/address.ts';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.ts';
import {
  SupporterAchFieldsEnum,
  SupporterCCFieldsEnum,
} from '@sola/schema/dotcom/supporters/paymentMethods.ts';
import type {
  GraphQLCommitment,
  GraphQLPaymentMethodBillingAddress,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { getLongExpiryDate } from '@sola/shared-utils/credit-cards.ts';
import { userErrorPaymentMethodUpdate } from '@sola/shared-utils/errors/supporters.ts';
import { getStreet } from '@sola/shared-utils/supporters/addresses.ts';
import { useSupporterBillingAddresses } from '@sola/supporter-sdk/hooks/payment-methods.ts';
import { getNonRecurringCommitments } from '@sola/supporter-sdk/utils/commitments.ts';

import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlert } from '../../molecules/local-alerts/local-alerts.tsx';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts.ts';
import { PaymentMethodUpdateForm } from '../../molecules/payment-method-update-form/payment-method-update-form.tsx';
import {
  createPaymentMethodPartialSuccessAlert,
  createPaymentMethodSuccessAlert,
  createSupportAlert,
} from '../../utils/alerts.tsx';
import { getErrorTranslationKeys } from '../../utils/errors.ts';
import { RecurringGivingRadioOptionsEnum } from '../recurring-giving-options/recurring-giving-options.tsx';

type PaymentMethodUpdateSheetFooterProps = Pick<FormSubmitProps, 'isBusy'>;

const PaymentMethodUpdateSheetFooter: React.FC<
  PaymentMethodUpdateSheetFooterProps
> = ({ isBusy }) => {
  const { t } = useTranslation('core');

  return (
    <SheetFooter>
      <SheetActions>
        <SheetClose asChild>
          <ButtonStyle
            size="lg"
            purpose="neutral"
            variant="secondary"
            disabled={isBusy}
            fullWidth
          >
            <button type="button">{t('core:actions.cancel')}</button>
          </ButtonStyle>
        </SheetClose>

        <FormSubmit
          iconBefore={<ArkIcon iconName="lock-01" />}
          isBusy={isBusy}
          disableUntilFilled
        >
          {t('core:actions.update')}
        </FormSubmit>
      </SheetActions>
    </SheetFooter>
  );
};

export interface PaymentMethodUpdateProps {
  openTrigger: ReactElement;
  paymentMethod: GraphQLSupporterPaymentMethod;
  commitments: GraphQLCommitment[];
  onCommitmentsUpdated?: (updatedCommitments: GraphQLCommitment[]) => void;
}

const useComponentData = ({
  paymentMethod,
  commitments,
  onCommitmentsUpdated,
}: Pick<
  PaymentMethodUpdateProps,
  'paymentMethod' | 'commitments' | 'onCommitmentsUpdated'
>) => {
  const { t } = useTranslation('my');
  const [isOpen, setIsOpen] = useState(false);
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const {
    alerts: sheetAlerts,
    setAlerts: setSheetAlerts,
    clearAlerts: clearSheetAlerts,
    removeAlert: removeSheetAlert,
    handleOpenChange,
  } = useModalAlerts();
  const updatePaymentMethod = usePaymentMethodUpdate();
  const { supporterAddresses } = useSupporterBillingAddresses();
  const nonRecurringCommitments = getNonRecurringCommitments(commitments);

  const updateAch = (
    data: FieldValues,
  ): GraphQLSupporterPaymentMethod | undefined => {
    if (!paymentMethod.ach) {
      return;
    }

    return {
      ...paymentMethod,
      accountNickname: data[SupporterAchFieldsEnum.nickname],
      ach: {
        ...paymentMethod.ach,
        accountName: data[SupporterAchFieldsEnum.accountName],
      },
    };
  };

  const updateCard = (
    data: FieldValues,
  ): GraphQLSupporterPaymentMethod | undefined => {
    if (!paymentMethod.creditCard) {
      return;
    }

    let billingAddress: GraphQLPaymentMethodBillingAddress;

    if (data[BillingFieldsEnum.AddressOption] === AddressSelectOptions.Add) {
      billingAddress = {
        street: data[BillingAddressEnum.Address],
        city: data[BillingAddressEnum.City],
        stateCode: data[BillingAddressEnum.State],
        postalCode: data[BillingAddressEnum.Postal],
        country: data[BillingAddressEnum.Country],
      };
    } else {
      // NEW CREDIT CARD WITHOUT NEW BILLING ADDRESS
      const selectedAddress = supporterAddresses?.get(
        data[BillingFieldsEnum.AddressOption],
      );

      if (!selectedAddress) {
        return;
      }

      const {
        billingAddress: billingAddress1,
        billingAddress2,
        billingCity: city,
        billingState: stateCode,
        billingPostal: postalCode,
        billingCountry: country,
      } = selectedAddress.billingAddress;
      billingAddress = {
        street: getStreet(billingAddress1, billingAddress2),
        city,
        stateCode,
        postalCode,
        country,
      };
    }

    const expirationDate = getLongExpiryDate(
      data[SupporterCCFieldsEnum.expirationDate],
    );

    return {
      ...paymentMethod,
      accountNickname: data[SupporterCCFieldsEnum.nickname],
      creditCard: {
        ...paymentMethod.creditCard,
        cardholderName: data[SupporterCCFieldsEnum.nameOnCard],
        expirationDate,
      },
      billingAddress,
    };
  };

  const handleSubmit = async (data: FieldValues) => {
    clearSheetAlerts();
    clearAlerts();
    try {
      const updatedPaymentMethod = paymentMethod.ach
        ? updateAch(data)
        : updateCard(data);

      if (!updatedPaymentMethod) {
        throw userErrorPaymentMethodUpdate();
      }

      let commitmentIds = [] as string[];

      if (data.recurringGiving === RecurringGivingRadioOptionsEnum.Yes) {
        commitmentIds =
          nonRecurringCommitments
            ?.map((commitment) => commitment.internalId)
            .filter((id): id is string => typeof id === 'string') ?? [];
      }

      const {
        paymentMethod: savedPaymentMethod,
        commitmentUpdates,
        isPartialSuccess,
      } = await updatePaymentMethod.mutateAsync({
        paymentMethod: updatedPaymentMethod,
        commitmentIds,
      });

      const alerts = [
        createPaymentMethodSuccessAlert(
          savedPaymentMethod,
          'updatePaymentMethod.success.action',
        ),
      ];

      if (isPartialSuccess) {
        alerts.push(createPaymentMethodPartialSuccessAlert());
      }

      addAlerts(alerts);
      onCommitmentsUpdated?.(commitmentUpdates?.updated ?? []);
      handleOpenChange(false, setIsOpen);
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(
        SupporterErrorTypeEnum.PaymentMethodUpdate,
        errors,
      );
      setSheetAlerts(
        translationKeys.map((translationKey) => {
          return createSupportAlert(translationKey);
        }),
      );
    }
  };

  return {
    t,
    isOpen,
    setIsOpen,
    handleOpenChange,
    sheetAlerts,
    removeSheetAlert,
    nonRecurringCommitments,
    handleSubmit,
    isUpdatePending: updatePaymentMethod.isPending,
  };
};

export const PaymentMethodUpdateSheet: React.FC<PaymentMethodUpdateProps> = ({
  openTrigger,
  paymentMethod,
  commitments,
  onCommitmentsUpdated,
}) => {
  const {
    t,
    isOpen,
    setIsOpen,
    handleOpenChange,
    sheetAlerts,
    removeSheetAlert,
    nonRecurringCommitments,
    handleSubmit,
    isUpdatePending,
  } = useComponentData({
    paymentMethod,
    commitments,
    onCommitmentsUpdated,
  });

  return (
    <Sheet
      open={isOpen}
      dismissible={!isUpdatePending}
      onOpenChange={(open) => handleOpenChange(open, setIsOpen)}
    >
      <SheetTrigger>{openTrigger}</SheetTrigger>
      <SheetContent title={t('updatePaymentMethod.form.title')}>
        <SheetHeader>{t('updatePaymentMethod.form.title')}</SheetHeader>
        <SheetBody>
          <p className="body-sm md:body-md body-neutral-mild">
            {t('updatePaymentMethod.form.leadin')}
          </p>

          {sheetAlerts.length > 0 && (
            <div className="mt-comfy-sm md:mt-comfy-lg">
              {sheetAlerts.map((sheetAlert) => (
                <LocalAlert
                  key={sheetAlert.id}
                  alert={sheetAlert}
                  className="my-comfy-xs"
                  onClose={removeSheetAlert}
                />
              ))}
            </div>
          )}

          <Divider
            purpose="neutral"
            className="mt-comfy-lg mb-comfy-xl md:mt-comfy-xl"
          />

          <PaymentMethodUpdateForm
            paymentMethod={paymentMethod}
            nonRecurringCommitments={nonRecurringCommitments}
            onSubmit={handleSubmit}
          >
            <PaymentMethodUpdateSheetFooter isBusy={isUpdatePending} />
          </PaymentMethodUpdateForm>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};
